import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is authenticated on app load
    useEffect(() => {
        let isMounted = true;
        
        const performAuthCheck = async () => {
            try {
                setLoading(true);
                const response = await authService.verifyToken();
                if (isMounted) {
                    if (response.success) {
                        setUser(response.user);
                        setIsAuthenticated(true);
                    } else {
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
            } catch (error) {
                // Only clear auth state on actual authentication errors
                if (isMounted) {
                    if (error.response?.status === 401) {
                        // Only clear on 401 Unauthorized
                        setUser(null);
                        setIsAuthenticated(false);
                    } else {
                        // For network errors, assume not authenticated but don't force logout
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        performAuthCheck();

        return () => {
            isMounted = false;
        };
    }, []); // Empty dependency array - only run once on mount

    const checkAuthStatus = async () => {
        try {
            setLoading(true);
            const response = await authService.verifyToken();
            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            // Only log out if it's a real authentication error, not a network error
            if (error.response?.status === 401) {
                // Only clear auth state on actual 401 Unauthorized
                setUser(null);
                setIsAuthenticated(false);
            }
            // For network errors, keep current auth state
        } finally {
            setLoading(false);
        }
    };

    const login = async (credentials) => {
        try {
            const response = await authService.login(credentials);
            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
                return response;
            }
            throw response;
        } catch (error) {
            throw error;
        }
    };

    const signup = async (userData) => {
        try {
            const response = await authService.signup(userData);
            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
                return response;
            }
            throw response;
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local state regardless of API call success
            setUser(null);
            setIsAuthenticated(false);
            
            // Clear any cached data
            localStorage.clear();
            sessionStorage.clear();
        }
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        signup,
        logout,
        checkAuthStatus
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};